import { Menu, Stack, Typography } from '@mui/material';
import { bindMenu, bindTrigger } from 'material-ui-popup-state/core';
import useDisableDocumentScroll from 'hooks/use-disable-document-scroll';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { SearchInput } from 'components/ui-new/inputs/search-input';
import { ReactSortable } from 'react-sortablejs';
import CustomizationItem from 'components/ui-new/customization-configuration-popup/components/customization-configuration-item/customization-configuration-item';
import { ConfigurationT, ItemOrHeader } from 'components/ui-new/customization-configuration-popup/types';
import {
  areIdsInSameOrder,
  getListWithHeaders,
  isAttributeItem,
  isHeaderItem,
} from 'components/ui-new/customization-configuration-popup/helpers';
import LoadingSkeleton from 'components/ui-new/customization-configuration-popup/components/loading-skeleton/loading-skeleton';
import NoResults from 'components/ui-new/customization-configuration-popup/components/no-results/no-results';
import useCustomizationConfigurationPopup from 'components/ui-new/customization-configuration-popup/use-customization-configuration-popup';
import useCustomizationConfigurationSearch from 'components/ui-new/customization-configuration-popup/use-customization-configuration-search';
import s from './customization-configuration-popup.module.scss';

type Props = {
  count: number;
  isLoading: boolean;
  children?: ReactNode;
  data: ConfigurationT[];
  disableDeleteAction: boolean;
  onPopupOpen?: () => void;
  onClickItem?: (item: ConfigurationT) => void;
  onDragAttributes?: (configurations: ConfigurationT[]) => void;
};

const CustomizationConfigurationPopup = ({
  data,
  count,
  children,
  isLoading,
  disableDeleteAction,
  onClickItem,
  onPopupOpen,
  onDragAttributes,
}: Props) => {
  const { isOpen, rootRef, popupState, setIsOpen } = useCustomizationConfigurationPopup({
    onPopupOpen,
    data,
  });

  useDisableDocumentScroll(isOpen, 'customization-dropdown');

  const [customizationList, setCustomizationList] = useState<ConfigurationT[]>([]);

  const { filtered, searchState, setSearch } = useCustomizationConfigurationSearch(customizationList);

  const listWithHeaders = useMemo(() => getListWithHeaders(customizationList, count), [customizationList]);

  const filteredListWithHeaders = useMemo(() => getListWithHeaders(filtered, count, true), [filtered]);

  const handleSetValue = useCallback(
    (list: ItemOrHeader[]) => {
      if (areIdsInSameOrder(list, customizationList)) return;

      const filteredItems = list.filter(isAttributeItem);

      const hiddenHeaderIndex = list.findIndex((item) => isHeaderItem(item) && item.id === 'header_hidden');

      const newList = filteredItems.map((item) => {
        const itemIndexInListWithHeaders = list.findIndex((l) => isAttributeItem(l) && l.id === item.id);

        const isShown = hiddenHeaderIndex === -1 ? true : itemIndexInListWithHeaders < hiddenHeaderIndex;

        return {
          ...item,
          is_shown: isShown,
        };
      });

      onDragAttributes?.(JSON.parse(JSON.stringify(newList)));
    },
    [onDragAttributes, customizationList],
  );

  const handleClickItem = (item: ConfigurationT) => {
    onClickItem?.(item);
  };

  const isDnDDisabled = !!searchState.trim();

  // This effect seems meaningless but it's required to display new item on appear.
  // Unfortunately, if you remove this effect and try to use tasks directly
  // you'll face an error in the console that a state mutation was detected between dispatches

  useEffect(() => {
    const current = JSON.stringify(customizationList);
    const next = JSON.stringify(data);
    if (current !== next) {
      setCustomizationList(JSON.parse(next));
    }
  }, [data, customizationList]);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  return (
    <div ref={rootRef} {...bindTrigger(popupState)}>
      <div onClick={() => setIsOpen((prev) => !prev)}>{children}</div>

      <Menu
        {...bindMenu(popupState)}
        open={isOpen}
        anchorEl={rootRef.current}
        className={s.menu_container}
        onClose={() => setIsOpen(false)}
        transformOrigin={{ vertical: 'top', horizontal: 350 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 350 }}
        slotProps={{
          paper: {
            sx: {
              overflow: 'visible',
              maxHeight: 'none',
            },
          },
        }}
      >
        {/* Don't delete this strange div! */}
        <div />
        <Typography color="#141414" fontWeight={600} fontSize="16px" marginBottom="12px">
          Production details
        </Typography>
        <Stack direction="row" gap={1} marginBottom="8px">
          <SearchInput
            fullWidth
            size="medium"
            value={searchState}
            placeholder="Search for an attribute"
            inputProps={{
              style: {
                height: '20px',
              },
            }}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Stack>
        {isLoading && <LoadingSkeleton count={count} />}

        {!isLoading && searchState && !filtered.length && <NoResults />}

        {!isLoading && (!searchState || filtered.length > 0) && (
          <div className={s.outer_container}>
            <ReactSortable
              list={listWithHeaders}
              setList={handleSetValue}
              group="attributes"
              animation={150}
              className={s.container}
              onEnd={(evnt, sortable) => sortable?.el.classList.add('use-hover')}
              onStart={(evnt, sortable) => sortable?.el.classList.remove('use-hover')}
              handle=".handle-area"
              disabled={isDnDDisabled}
            >
              {(searchState ? filteredListWithHeaders : listWithHeaders).map((item) => {
                if (isHeaderItem(item)) {
                  return (
                    <div
                      key={item.id}
                      className={s.group_header}
                      style={{ cursor: 'default', userSelect: 'none' }}
                      data-draggable={false}
                    >
                      {item.label}
                    </div>
                  );
                }

                return (
                  <div
                    key={item.id}
                    className={`${s.items_container} ${disableDeleteAction && item.is_shown ? '' : 'handle-area'}`}
                  >
                    <CustomizationItem
                      name={item.name}
                      is_shown={item.is_shown}
                      disableDeleteAction={disableDeleteAction && item.is_shown}
                      onClickIcon={() => isAttributeItem(item) && handleClickItem(item)}
                    />
                  </div>
                );
              })}
            </ReactSortable>
          </div>
        )}
      </Menu>
    </div>
  );
};

export default CustomizationConfigurationPopup;
